// Two-factor authentication for a player (spec/07 §6.6, F-IAM-09's endpoints): turn it on (the password, then an
// authenticator set up from a QR code or the key, the backup codes shown once, the first code to prove it), make new
// backup codes, or turn it off. better-auth's own endpoints, in the contract. Two-factor guards password sign-in, so
// an account with no password is asked to set one first.

import { useTranslation } from '@clavian/i18n/react';
import { Badge, Button, Dialog, encodeQr, qrPath, TextField, useToast } from '@clavian/ui-web';
import { useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import { useApi } from '../api/client';
import { useAuthFailure } from '../auth/auth-errors';
import { queryKeys, useLinkedAccounts, useSession } from '../api/queries';

type Flow =
  | { readonly kind: 'idle' }
  | { readonly kind: 'password'; readonly purpose: 'enable' | 'disable' | 'codes' }
  | { readonly kind: 'setup'; readonly uri: string; readonly secret: string; readonly codes: readonly string[] }
  | { readonly kind: 'codes'; readonly codes: readonly string[] };

/** The authenticator link as a QR code: crisp SVG, one path, no dependency. */
function QrCode({ text, label }: { readonly text: string; readonly label: string }): ReactNode {
  const code = encodeQr(text);
  const quiet = 4;
  const size = code.size + quiet * 2;
  return (
    <svg className="pw-qr" viewBox={`0 0 ${size} ${size}`} role="img" aria-label={label} shapeRendering="crispEdges">
      <rect width={size} height={size} fill="#ffffff" />
      <path d={qrPath(code)} transform={`translate(${quiet} ${quiet})`} fill="#000000" />
    </svg>
  );
}

function BackupCodes({ codes }: { readonly codes: readonly string[] }): ReactNode {
  const { t } = useTranslation('player');
  const { show } = useToast();
  return (
    <div className="pw-codes">
      <ul className="pw-codes__list">
        {codes.map((code) => (
          <li key={code} className="clav-num">
            {code}
          </li>
        ))}
      </ul>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => {
          void navigator.clipboard
            ?.writeText(codes.join('\n'))
            .then(() => show({ title: t('account.twoFactor.copied'), tone: 'ok' }))
            .catch(() => undefined);
        }}
      >
        {t('account.twoFactor.copy')}
      </Button>
    </div>
  );
}

export function TwoFactorSection(): ReactNode {
  const { t } = useTranslation('player');
  const api = useApi();
  const queryClient = useQueryClient();
  const failureOf = useAuthFailure();
  const { show } = useToast();
  const session = useSession();
  const accounts = useLinkedAccounts();
  const enabled = session.data?.user.twoFactorEnabled === true;
  const hasPassword = (accounts.data ?? []).some((account) => account.providerId === 'credential');
  const [flow, setFlow] = useState<Flow>({ kind: 'idle' });
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const close = () => {
    setFlow({ kind: 'idle' });
    setPassword('');
    setCode('');
    setFailure(null);
  };
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.session });
    await queryClient.refetchQueries({ queryKey: queryKeys.session });
  };
  const failed = (error: unknown) => {
    setFailure(failureOf(error, { SESSION_NOT_FRESH: 'account.twoFactor.fresh', INVALID_CODE: 'account.twoFactor.wrongCode' }));
  };

  const submitPassword = async (purpose: 'enable' | 'disable' | 'codes') => {
    setFailure(null);
    setBusy(true);
    try {
      if (purpose === 'enable') {
        const result = await api.enableTwoFactor({ body: { password } });
        setFlow({ kind: 'setup', uri: result.totpURI, secret: new URL(result.totpURI).searchParams.get('secret') ?? '', codes: result.backupCodes });
      } else if (purpose === 'disable') {
        await api.disableTwoFactor({ body: { password } });
        await refresh();
        close();
        show({ title: t('account.twoFactor.disabled'), tone: 'ok' });
      } else {
        const result = await api.generateTwoFactorBackupCodes({ body: { password } });
        setFlow({ kind: 'codes', codes: result.backupCodes });
      }
      setPassword('');
    } catch (error) {
      failed(error);
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setFailure(null);
    setBusy(true);
    try {
      await api.verifyTwoFactorTotp({ body: { code: code.trim() } });
      await refresh();
      close();
      show({ title: t('account.twoFactor.enabled'), tone: 'ok' });
    } catch (error) {
      failed(error);
    } finally {
      setBusy(false);
    }
  };

  const purpose = flow.kind === 'password' ? flow.purpose : null;

  return (
    <section className="pw-panel" aria-labelledby="pw-2fa">
      <div className="pw-section-head">
        <h2 className="pw-section-title" id="pw-2fa">
          {t('account.twoFactor.title')}
        </h2>
        <Badge tone={enabled ? 'ok' : 'neutral'} dot>
          {enabled ? t('account.twoFactor.on') : t('account.twoFactor.off')}
        </Badge>
      </div>
      <p className="pw-muted">{enabled ? t('account.twoFactor.bodyOn') : hasPassword ? t('account.twoFactor.bodyOff') : t('account.twoFactor.needsPassword')}</p>
      <div className="pw-form__actions">
        {enabled ? (
          <>
            <Button variant="secondary" onClick={() => setFlow({ kind: 'password', purpose: 'codes' })}>
              {t('account.twoFactor.newCodes')}
            </Button>
            <Button variant="ghost" onClick={() => setFlow({ kind: 'password', purpose: 'disable' })}>
              {t('account.twoFactor.turnOff')}
            </Button>
          </>
        ) : hasPassword ? (
          <Button variant="primary" onClick={() => setFlow({ kind: 'password', purpose: 'enable' })}>
            {t('account.twoFactor.turnOn')}
          </Button>
        ) : null}
      </div>

      <Dialog
        open={purpose !== null}
        onOpenChange={(open) => (open ? undefined : close())}
        title={purpose === 'disable' ? t('account.twoFactor.offTitle') : purpose === 'codes' ? t('account.twoFactor.newCodesTitle') : t('account.twoFactor.setupTitle')}
        description={purpose === 'disable' ? t('account.twoFactor.offBody') : purpose === 'codes' ? t('account.twoFactor.newCodesBody') : undefined}
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={close}>
              {t('account.twoFactor.cancel')}
            </Button>
            <Button
              variant={purpose === 'disable' ? 'danger' : 'primary'}
              busy={busy}
              disabled={password === ''}
              onClick={() => (purpose === null ? undefined : void submitPassword(purpose))}
            >
              {purpose === 'disable' ? t('account.twoFactor.turnOff') : t('account.twoFactor.start')}
            </Button>
          </>
        }
      >
        <form
          className="pw-form"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            if (purpose !== null && password !== '') void submitPassword(purpose);
          }}
        >
          {failure === null ? null : (
            <p className="pw-alert" role="alert">
              {failure}
            </p>
          )}
          <TextField
            label={t('account.twoFactor.password')}
            description={t('account.twoFactor.passwordHint')}
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </form>
      </Dialog>

      <Dialog
        open={flow.kind === 'setup'}
        onOpenChange={(open) => (open ? undefined : close())}
        title={t('account.twoFactor.setupTitle')}
        size="md"
        dismissible={false}
        footer={
          <>
            <Button variant="ghost" onClick={close}>
              {t('account.twoFactor.cancel')}
            </Button>
            <Button variant="primary" busy={busy} disabled={code.trim().length < 6} onClick={() => void verify()}>
              {t('account.twoFactor.verify')}
            </Button>
          </>
        }
      >
        {flow.kind === 'setup' ? (
          <div className="pw-setup">
            <p>{t('account.twoFactor.scan')}</p>
            <div className="pw-setup__qr">
              <QrCode text={flow.uri} label={t('account.twoFactor.qrLabel')} />
            </div>
            <p className="pw-subtle">{t('account.twoFactor.key')}</p>
            <p className="pw-setup__key clav-num">{flow.secret}</p>
            <p>
              <a className="pw-link" href={flow.uri}>
                {t('account.twoFactor.openLink')}
              </a>
            </p>
            <h3 className="pw-setup__title">{t('account.twoFactor.backupTitle')}</h3>
            <p className="pw-subtle">{t('account.twoFactor.backupBody')}</p>
            <BackupCodes codes={flow.codes} />
            {failure === null ? null : (
              <p className="pw-alert" role="alert">
                {failure}
              </p>
            )}
            <TextField
              label={t('account.twoFactor.code')}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D+/g, ''))}
            />
          </div>
        ) : null}
      </Dialog>

      <Dialog
        open={flow.kind === 'codes'}
        onOpenChange={(open) => (open ? undefined : close())}
        title={t('account.twoFactor.backupTitle')}
        description={t('account.twoFactor.backupBody')}
        size="sm"
        footer={
          <Button variant="primary" onClick={close}>
            {t('account.twoFactor.done')}
          </Button>
        }
      >
        {flow.kind === 'codes' ? <BackupCodes codes={flow.codes} /> : null}
      </Dialog>
    </section>
  );
}
